import React, { useState, useEffect, useRef } from 'react'
import { Form, FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'
import Link from 'next/link'
import { Turnstile } from '@marsidev/react-turnstile'
import type { TurnstileInstance } from '@marsidev/react-turnstile'

import timezones from 'constants/timezones'
import { signupSchema } from 'lib/validation-schema'

import Button from 'components/Button'
import { FormField } from 'components/FormControls'
import { FormErrorContainer } from 'components/FormControls'
import { InputGroup } from 'components/FormContainers'

import EmailAndPhoneTakenMessage from './EmailAndPhoneTakenMessage'
import useSignupUserForm from './useSignupUserform'

import { UserSignupSourceEnum } from '../../../../../__generated__/globalTypes'

import styles from './SignupUserForm.module.css'

type SignupUserFormProps = {
  signupSource: UserSignupSourceEnum | undefined
  emailPrefillValue?: string
  onSignupSuccess: () => void
  loginPath?: string
}

const SignupUserForm = ({ signupSource, emailPrefillValue, onSignupSuccess, loginPath }: SignupUserFormProps) => {
  const [localTimezone, setLocalTimezone] = useState<string | undefined>(undefined)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState<string | null>(null)
  const [, setTurnstileStatus] = useState<'idle' | 'loading' | 'solved' | 'error' | 'expired'>('idle')
  const turnstileRef = useRef<TurnstileInstance | null>(null)

  const turnstileSiteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY || ''

  const { initialValues, onSubmit } = useSignupUserForm({
    signupSource,
    emailPrefillValue,
    localTimezone,
    onSignupSuccess,
    turnstileToken
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validateOnBlur: false,
    validationSchema: signupSchema,
    onSubmit: async (values, helpers) => {
      if (!turnstileToken) {
        setTurnstileError('Please complete the security challenge')
        helpers.setSubmitting(false)
        return
      }
      setTurnstileError(null)
      return onSubmit(values, helpers)
    }
  })

  const { errors, isSubmitting, submitCount } = formik

  useEffect(() => {
    const localTz = DateTime.local().zoneName
    const validTz = timezones.find(tz => tz === localTz)
    setLocalTimezone(validTz || 'Australia/Melbourne')
  }, [])

  return (
    <FormikProvider value={formik}>
      <Form aria-labelledby="signup-heading">
        {[errors.email, errors.phone].includes('has already been taken') && (
          <EmailAndPhoneTakenMessage loginPath={loginPath} errors={errors} />
        )}
        <InputGroup>
          <div className={styles.nameRow}>
            <FormField name="firstName" title="First Name*" type="text" autoFocus />
            <FormField name="lastName" title="Last Name*" type="text" />
          </div>

          <FormField name="email" title="Email*" type="email" autoComplete="username" />
          <FormField name="phone" title="Phone*" type="tel" usePriorityCountries />
          <FormField name="password" title="Password*" type="password" autoComplete="new-password" />
          <FormField name="termsAccepted" type="checkbox">
            <small>
              I have read and agree to the Fieldfolio
              <br />
              <Link href="/terms">
                <a target="_blank">Terms of Service</a>
              </Link>{' '}
              and{' '}
              <Link href="/privacy">
                <a target="_blank">Privacy Policy</a>
              </Link>
              .
            </small>
          </FormField>

          {errors.termsAccepted && submitCount > 0 && (
            <FormErrorContainer>
              <small>{errors.termsAccepted}</small>
            </FormErrorContainer>
          )}

          {turnstileSiteKey && (
            <Turnstile
              ref={turnstileRef}
              siteKey={turnstileSiteKey}
              options={{ theme: 'light', size: 'flexible' }}
              onSuccess={(token: string) => {
                setTurnstileToken(token)
                setTurnstileStatus('solved')
                setTurnstileError(null)
              }}
              onError={() => {
                setTurnstileToken(null)
                setTurnstileStatus('error')
                setTurnstileError('Security verification failed. Please try again.')
              }}
              onExpire={() => {
                setTurnstileToken(null)
                setTurnstileStatus('expired')
                setTurnstileError('Security verification expired. Please try again.')
              }}
              onBeforeInteractive={() => {
                setTurnstileStatus('loading')
              }}
            />
          )}

          {turnstileError && submitCount > 0 && (
            <FormErrorContainer>
              <small>{turnstileError}</small>
            </FormErrorContainer>
          )}
        </InputGroup>

        <Button isBlock kind="primary" type="submit" disabled={isSubmitting} isSubmitting={isSubmitting}>
          Sign Up
        </Button>
      </Form>
    </FormikProvider>
  )
}

export default SignupUserForm
